import { useState, useEffect, useRef } from "react";

const SYSTEM_PROMPT = `You are an elite Prompt Engineer. Transform the user's raw prompt into a professional, structured version with: expert role, clear task, format specs, output structure, and quality criteria. Output ONLY the enhanced prompt. No preamble. No explanation.`;

// ✅ Groq API — 100% FREE + Ultra Fast (500+ tokens/sec)
// Key lene ke liye: https://console.groq.com → API Keys → Create Key
const GROQ_API_KEY = "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"; // 👈 APNI KEY YAHAN DAALO
const GROQ_MODEL = "llama-3.3-70b-versatile"; // Most powerful free model

const SUGGESTIONS = [
  "write a blog post about AI",
  "help me with my resume",
  "explain quantum computing",
  "create a workout plan for me",
  "write a python web scraper",
  "make a marketing email",
];

export default function PromptEnhancer() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [error, setError] = useState("");
  const [animateIn, setAnimateIn] = useState(false);
  const outputRef = useRef(null);

  useEffect(() => { setCharCount(input.length); }, [input]);

  useEffect(() => {
    if (output) {
      setAnimateIn(false);
      setTimeout(() => setAnimateIn(true), 50);
    }
  }, [output]);

  async function handleEnhance() {
    if (!input.trim()) return;
    setLoading(true);
    setError("");
    setOutput("");

    try {
      const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${GROQ_API_KEY}`,
        },
        body: JSON.stringify({
          model: GROQ_MODEL,
          max_tokens: 600,
          temperature: 0.7,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            { role: "user", content: `Enhance this prompt:\n\n${input}` },
          ],
        }),
      });

      const data = await response.json();
      if (data.error) throw new Error(data.error.message);
      const text = data.choices?.[0]?.message?.content || "";
      setOutput(text.trim());
    } catch (e) {
      setError("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = output;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleSuggestion(s) {
    setInput(s);
    setOutput("");
  }

  const ratio = output
    ? Math.round((output.length / Math.max(input.length, 1)) * 10) / 10
    : null;

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0f",
      fontFamily: "'Georgia', 'Times New Roman', serif",
      color: "#e8e4da",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Background */}
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        background: `
          radial-gradient(ellipse 60% 40% at 20% 20%, rgba(255,180,50,0.04) 0%, transparent 70%),
          radial-gradient(ellipse 50% 60% at 80% 80%, rgba(120,80,200,0.05) 0%, transparent 70%),
          radial-gradient(ellipse 80% 30% at 50% 50%, rgba(30,30,60,0.8) 0%, transparent 100%)
        `,
      }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 820, margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div style={{
            display: "inline-block", fontSize: 11, letterSpacing: "0.3em",
            textTransform: "uppercase", color: "#c8a84b", marginBottom: 20,
          }}>
            ✦ Prompt Intelligence ✦
          </div>
          <h1 style={{
            fontSize: "clamp(36px, 6vw, 68px)", fontWeight: 400,
            margin: "0 0 16px", lineHeight: 1.1, letterSpacing: "-0.02em", color: "#f0ebe0",
          }}>
            Prompt <em style={{ color: "#c8a84b", fontStyle: "italic" }}>Enhancer</em>
          </h1>
          <p style={{ fontSize: 16, color: "#8a8070", margin: 0, maxWidth: 400, marginLeft: "auto", marginRight: "auto", lineHeight: 1.6 }}>
            Transform any simple prompt into a professional, structured masterpiece
          </p>
          <div style={{
            display: "inline-block", marginTop: 14,
            background: "rgba(100,200,100,0.1)", border: "1px solid rgba(100,200,100,0.3)",
            borderRadius: 20, padding: "4px 16px", fontSize: 11, color: "#80d080", letterSpacing: "0.1em",
          }}>
            ⚡ 100% FREE — Groq + Llama 3.3 70B
          </div>
        </div>

        {/* Suggestion Pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24, justifyContent: "center" }}>
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => handleSuggestion(s)}
              style={{
                background: "rgba(200,168,75,0.08)", border: "1px solid rgba(200,168,75,0.2)",
                color: "#c8a84b", borderRadius: 20, padding: "6px 14px", fontSize: 12,
                cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em", transition: "all 0.2s",
              }}
              onMouseEnter={e => { e.target.style.background = "rgba(200,168,75,0.15)"; e.target.style.borderColor = "rgba(200,168,75,0.4)"; }}
              onMouseLeave={e => { e.target.style.background = "rgba(200,168,75,0.08)"; e.target.style.borderColor = "rgba(200,168,75,0.2)"; }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Input */}
        <div style={{
          background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12, padding: 24, marginBottom: 16,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <label style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6a6055" }}>Your Prompt</label>
            <span style={{ fontSize: 12, color: charCount > 0 ? "#6a6055" : "#3a3530" }}>{charCount} chars</span>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type any prompt here — vague, short, or rough. I'll make it 10x better..."
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleEnhance(); }}
            style={{
              width: "100%", minHeight: 120, background: "transparent", border: "none",
              outline: "none", color: "#e8e4da", fontSize: 15, fontFamily: "inherit",
              lineHeight: 1.7, resize: "vertical", boxSizing: "border-box",
            }}
          />
          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 11, color: "#3a3530" }}>Ctrl + Enter to enhance</span>
            <button
              onClick={handleEnhance}
              disabled={!input.trim() || loading}
              style={{
                background: input.trim() && !loading ? "linear-gradient(135deg, #c8a84b, #e8c96a)" : "rgba(100,90,70,0.3)",
                border: "none", borderRadius: 8, padding: "12px 32px",
                color: input.trim() && !loading ? "#1a1408" : "#5a5040",
                fontSize: 14, fontWeight: 600, fontFamily: "'Georgia', serif",
                cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                letterSpacing: "0.05em", transition: "all 0.25s",
              }}
            >
              {loading ? "⚡ Enhancing..." : "✦ Enhance Prompt"}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: "rgba(200,60,60,0.1)", border: "1px solid rgba(200,60,60,0.3)",
            borderRadius: 8, padding: "12px 16px", color: "#e08080", fontSize: 14, marginBottom: 16,
          }}>
            ⚠ {error}
          </div>
        )}

        {/* Output */}
        {output && (
          <div ref={outputRef} style={{
            background: "rgba(200,168,75,0.04)", border: "1px solid rgba(200,168,75,0.15)",
            borderRadius: 12, padding: 24,
            opacity: animateIn ? 1 : 0,
            transform: animateIn ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.4s ease, transform 0.4s ease",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#c8a84b" }}>
                  Enhanced Prompt
                </span>
                {ratio && (
                  <span style={{
                    background: "rgba(200,168,75,0.15)", border: "1px solid rgba(200,168,75,0.3)",
                    borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#c8a84b",
                  }}>
                    {ratio}× expanded
                  </span>
                )}
              </div>
              <button onClick={handleCopy} style={{
                background: copied ? "rgba(100,200,100,0.15)" : "rgba(200,168,75,0.1)",
                border: `1px solid ${copied ? "rgba(100,200,100,0.4)" : "rgba(200,168,75,0.25)"}`,
                borderRadius: 6, padding: "6px 14px",
                color: copied ? "#80d080" : "#c8a84b",
                fontSize: 12, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
              }}>
                {copied ? "✓ Copied!" : "📋 Copy"}
              </button>
            </div>
            <div style={{ color: "#d8d4c8", fontSize: 14, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "'Georgia', serif" }}>
              {output}
            </div>
            <div style={{
              marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.05)",
              fontSize: 12, color: "#5a5550",
            }}>
              ✦ Paste into ChatGPT, Claude, Gemini, or any AI tool
            </div>
          </div>
        )}

      </div>

      <style>{`
        * { box-sizing: border-box; }
        textarea::placeholder { color: #3a3530; }
      `}</style>
    </div>
  );
}